<template>
    <div class="crm-dashboard">
      <header class="dashboard-header">
        <div class="header-left">
          <woot-sidemenu-icon />
          <h1 class="page-title">{{ $t('CRM_DASHBOARD.TITLE') }}</h1>
        </div>
      </header>
  
      <div class="filters-section">
        <div class="filter-group">
          <label>{{ $t('CRM_DASHBOARD.FILTERS.KANBAN') }}</label>
          <select v-model="selectedKanban" @change="fetchMetrics">
            <option value="">{{ $t('CRM_DASHBOARD.FILTERS.ALL_KANBANS') }}</option>
            <option v-for="kanban in kanbanListActive" :key="kanban.id" :value="kanban.id">
              {{ kanban.name }}
            </option>
          </select>
        </div>
  
        <div class="filter-group">
          <label>{{ $t('CRM_DASHBOARD.FILTERS.AGENT') }}</label>
          <select v-model="selectedAgent" @change="fetchMetrics">
            <option value="">{{ $t('CRM_DASHBOARD.FILTERS.ALL_AGENTS') }}</option>
            <option v-for="agent in agents" :key="agent.id" :value="agent.id">
              {{ agent.name }}
            </option>
          </select>
        </div>
  
        <div class="filter-group">
          <label>{{ $t('CRM_DASHBOARD.FILTERS.PERIOD') }}</label>
          <select v-model="selectedPeriod" @change="handlePeriodChange">
            <option value="7">{{ $t('CRM_DASHBOARD.FILTERS.LAST_7_DAYS') }}</option>
            <option value="30">{{ $t('CRM_DASHBOARD.FILTERS.LAST_30_DAYS') }}</option>
            <option value="90">{{ $t('CRM_DASHBOARD.FILTERS.LAST_90_DAYS') }}</option>
            <option value="custom">{{ $t('CRM_DASHBOARD.FILTERS.CUSTOM') }}</option>
          </select>
        </div>
  
        <div v-if="selectedPeriod === 'custom'" class="filter-group date-range">
          <div class="date-input">
            <label>{{ $t('CRM_DASHBOARD.FILTERS.START_DATE') }}</label>
            <input type="date" v-model="startDate" @change="fetchMetrics" />
          </div>
          <div class="date-input">
            <label>{{ $t('CRM_DASHBOARD.FILTERS.END_DATE') }}</label>
            <input type="date" v-model="endDate" @change="fetchMetrics" />
          </div>
        </div>
      </div>
  
      <div v-if="loading" class="loading-state">
        <spinner />
        <p>{{ $t('CRM_DASHBOARD.LOADING') }}</p>
      </div>
  
      <div v-else class="dashboard-content">
        <div class="metrics-grid">
          <div class="metric-card">
            <div class="metric-icon tickets-icon">
              <fluent-icon icon="ticket" size="24" />
            </div>
            <div class="metric-info">
              <span class="metric-value">{{ metrics.total_tickets || 0 }}</span>
              <span class="metric-label">{{ $t('CRM_DASHBOARD.METRICS.TOTAL_TICKETS') }}</span>
            </div>
          </div>
  
          <div class="metric-card">
            <div class="metric-icon open-icon">
              <fluent-icon icon="clock" size="24" />
            </div>
            <div class="metric-info">
              <span class="metric-value">{{ metrics.open_tickets || 0 }}</span>
              <span class="metric-label">{{ $t('CRM_DASHBOARD.METRICS.OPEN_TICKETS') }}</span>
            </div>
          </div>
  
          <div class="metric-card">
            <div class="metric-icon critical-icon">
              <fluent-icon icon="warning" size="24" />
            </div>
            <div class="metric-info">
              <span class="metric-value">{{ metrics.critical_priority_tickets || 0 }}</span>
              <span class="metric-label">{{ $t('CRM_DASHBOARD.PRIORITY_LONG.CRITICAL') }}</span>
            </div>
          </div>
  
          <div class="metric-card">
            <div class="metric-icon high-icon">
              <fluent-icon icon="arrow-up" size="24" />
            </div>
            <div class="metric-info">
              <span class="metric-value">{{ metrics.high_priority_tickets || 0 }}</span>
              <span class="metric-label">{{ $t('CRM_DASHBOARD.METRICS.HIGH_PRIORITY') }}</span>
            </div>
          </div>
        </div>
  
        <div class="charts-section">
          <div class="chart-card">
            <h3 class="chart-title">{{ $t('CRM_DASHBOARD.CHARTS.TICKETS_BY_PERIOD') }}</h3>
            <div class="chart-container">
              <bar-chart 
                v-if="hasTicketsByPeriodData"
                :collection="ticketsByPeriodData" 
                :chart-options="barChartOptions"
              />
              <p v-else class="no-data">{{ $t('CRM_DASHBOARD.NO_DATA') }}</p>
            </div>
          </div>
  
          <div class="chart-card">
            <h3 class="chart-title">{{ $t('CRM_DASHBOARD.CHARTS.TICKETS_BY_STAGE') }}</h3>
            <div class="chart-container">
              <bar-chart
                v-if="hasTicketsByStageData"
                :collection="ticketsByStageData"
                :chart-options="horizontalBarChartOptions"
              />
              <p v-else class="no-data">{{ $t('CRM_DASHBOARD.NO_DATA') }}</p>
            </div>
          </div>
  
          <div class="chart-card small-chart">
            <h3 class="chart-title">{{ $t('CRM_DASHBOARD.CHARTS.TICKETS_BY_PRIORITY') }}</h3>
            <div class="priority-list">
              <div class="priority-item">
                <span class="priority-badge critical">{{ $t('CRM_DASHBOARD.PRIORITY_LABELS.CRITICAL') }}</span>
                <span class="priority-count">{{ metrics.critical_priority_tickets || 0 }}</span>
              </div>
              <div class="priority-item">
                <span class="priority-badge high">{{ $t('CRM_DASHBOARD.PRIORITY_LABELS.HIGH') }}</span>
                <span class="priority-count">{{ metrics.high_priority_tickets || 0 }}</span>
              </div>
              <div class="priority-item">
                <span class="priority-badge medium">{{ $t('CRM_DASHBOARD.PRIORITY_LABELS.MEDIUM') }}</span>
                <span class="priority-count">{{ metrics.medium_priority_tickets || 0 }}</span>
              </div>
              <div class="priority-item">
                <span class="priority-badge low">{{ $t('CRM_DASHBOARD.PRIORITY_LABELS.LOW') }}</span>
                <span class="priority-count">{{ metrics.low_priority_tickets || 0 }}</span>
              </div>
            </div>
          </div>
  
          <div class="chart-card small-chart">
            <h3 class="chart-title">{{ $t('CRM_DASHBOARD.CHARTS.TOP_AGENTS') }}</h3>
            <div class="agents-list">
              <div 
                v-for="(agent, index) in topAgents" 
                :key="agent.id" 
                class="agent-item"
              >
                <span class="agent-rank">{{ index + 1 }}º</span>
                <woot-thumbnail :src="agent.thumbnail" :username="agent.name" size="28px" />
                <span class="agent-name">{{ agent.name }}</span>
                <span class="agent-tickets">{{ agent.tickets_count }} tickets</span>
              </div>
              <p v-if="!topAgents.length" class="no-data">{{ $t('CRM_DASHBOARD.NO_DATA') }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </template>
  
  <script>
  import { mapGetters } from 'vuex';
  import Spinner from 'shared/components/Spinner.vue';
  import BarChart from '../../../../components/widgets/chart/BarChart';
  import { formatCurrency } from '../../../../helper/formatCurrency';
  import ticketApi from '../../../../api/ticket';
  import adminMixin from 'dashboard/mixins/isAdmin';
  
  export default {
    name: 'CrmDashboardView',
    components: {
      Spinner,
      BarChart,
    },
    mixins: [adminMixin],
  
    data() {
      return {
        loading: false,
        selectedKanban: '',
        selectedAgent: '',
        selectedPeriod: '30',
        startDate: '',
        endDate: '',
        metrics: {},
        ticketsByPeriod: [],
        ticketsByStage: [],
        topAgents: [],
        barChartOptions: {
          responsive: true,
          maintainAspectRatio: false,
          legend: { display: false },
          layout: {
            padding: {
              top: 20,
              bottom: 20,
              left: 20,
              right: 20
            }
          },
          scales: {
            yAxes: [{
              ticks: { beginAtZero: true, stepSize: 1 },
              gridLines: { drawOnChartArea: false }
            }],
            xAxes: [{
              gridLines: { drawOnChartArea: false }
            }]
          }
        },
        horizontalBarChartOptions: {
          responsive: true,
          maintainAspectRatio: false,
          legend: { display: false },
          layout: {
            padding: {
              top: 10,
              bottom: 10,
              left: 10,
              right: 10
            }
          },
          scales: {
            xAxes: [{
              ticks: {
                beginAtZero: true,
                stepSize: 1,
                maxTicksLimit: 5
              },
              gridLines: {
                drawOnChartArea: false,
                drawBorder: false
              },
              stacked: false
            }],
            yAxes: [{
              ticks: {
                maxTicksLimit: 10
              },
              gridLines: {
                drawOnChartArea: false,
                drawBorder: false
              },
              stacked: false
            }]
          },
          plugins: {
            datalabels: {
              display: false
            }
          }
        }
      };
    },
  
    computed: {
      ...mapGetters({
        currentUser: 'getCurrentUser',
        accountId: 'getCurrentAccountId',
        kanbanList: 'getKanbanfunnels',
        agents: 'agents/getAgents',
      }),
  
      kanbanListActive() {
        const kanbans = Array.isArray(this.kanbanList) ? this.kanbanList : [];
        const listActive = kanbans.filter(kanban => kanban && kanban.active);
        return listActive;
      },
      hasTicketsByPeriodData() {
        return this.ticketsByPeriodData && 
               this.ticketsByPeriodData.labels && 
               this.ticketsByPeriodData.labels.length > 0;
      },
      hasTicketsByStageData() {
        return this.ticketsByStageData && 
                this.ticketsByStageData.labels && 
                this.ticketsByStageData.labels.length > 0;
      },
  
      ticketsByPeriodData() {
        if (!this.ticketsByPeriod.length) return { labels: [], datasets: [] };
        
        return {
          labels: this.ticketsByPeriod.map(item => item.date),
          datasets: [{
            label: this.$t('CRM_DASHBOARD.CHARTS.TICKETS'),
            data: this.ticketsByPeriod.map(item => item.count),
            backgroundColor: 'rgba(59, 130, 246, 0.8)',
            borderColor: 'rgb(59, 130, 246)',
            borderWidth: 1
          }]
        };
      },
  
      ticketsByStageData() {
        if (!this.ticketsByStage.length) return { labels: [], datasets: [] };
        
        return {
          labels: this.ticketsByStage.map(item => item.name),
          datasets: [{
            label: this.$t('CRM_DASHBOARD.CHARTS.TICKETS'),
            data: this.ticketsByStage.map(item => item.count),
            backgroundColor: this.ticketsByStage.map((_, i) => this.getStageColor(i)),
            borderWidth: 0
          }]
        };
      }
    },
  
    mounted() {
      this.initializeDates();
      this.$store.dispatch('agents/get');
      this.$store.dispatch('fetchKanbanfunnels');
      this.fetchMetrics();
    },
  
    methods: {
      formatCurrency,
  
      initializeDates() {
        const end = new Date();
        const start = new Date();
        start.setDate(start.getDate() - 30);
        
        this.endDate = end.toISOString().split('T')[0];
        this.startDate = start.toISOString().split('T')[0];
      },
  
      handlePeriodChange() {
        if (this.selectedPeriod !== 'custom') {
          const end = new Date();
          const start = new Date();
          start.setDate(start.getDate() - parseInt(this.selectedPeriod));
          
          this.endDate = end.toISOString().split('T')[0];
          this.startDate = start.toISOString().split('T')[0];
          this.fetchMetrics();
        }
      },
  
      async fetchMetrics() {
        this.loading = true;
        
        try {
          const params = {
            accountId: this.accountId,
            start_date: this.startDate,
            end_date: this.endDate,
          };

          if (this.selectedKanban && this.selectedKanban !== '') {
            params.kanbanfunnel_id = this.selectedKanban;
          }
          if (this.selectedAgent && this.selectedAgent !== '') {
            params.agent_id = this.selectedAgent;
          }
  
          const response = await ticketApi.getDashboardMetrics(params);
          const data = response.data;
          
          this.metrics = data.metrics || this.getMockMetrics();
          this.ticketsByPeriod = data.tickets_by_period || this.getMockTicketsByPeriod();
          this.ticketsByStage = data.tickets_by_stage || this.getMockTicketsByStage();
          this.topAgents = data.top_agents || [];
          
        } catch (error) {
          this.metrics = this.getMockMetrics();
          this.ticketsByPeriod = this.getMockTicketsByPeriod();
          this.ticketsByStage = this.getMockTicketsByStage();
          this.topAgents = [];
        } finally {
          this.loading = false;
        }
      },
  
      getMockMetrics() {
        return {
          total_tickets: 0,
          open_tickets: 0,
          closed_tickets: 0,
          total_sales_value: 0,
          high_priority_tickets: 0,
          medium_priority_tickets: 0,
          low_priority_tickets: 0,
          avg_resolution_days: 0
        };
      },
  
      getMockTicketsByPeriod() {
        return [];
      },
  
      getMockTicketsByStage() {
        return [];
      },
  
      formatDays(days) {
        if (!days || days === 0) return '-';
        if (days < 1) return '< 1 dia';
        return `${Math.round(days)} dias`;
      },
  
      getStageColor(index) {
        const colors = [
          'rgba(59, 130, 246, 0.8)',
          'rgba(16, 185, 129, 0.8)',
          'rgba(245, 158, 11, 0.8)',
          'rgba(239, 68, 68, 0.8)',
          'rgba(139, 92, 246, 0.8)',
          'rgba(236, 72, 153, 0.8)',
          'rgba(20, 184, 166, 0.8)',
          'rgba(249, 115, 22, 0.8)',
        ];
        return colors[index % colors.length];
      }
    }
  };
  </script>
  
  <style lang="scss" scoped>
  .crm-dashboard {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    width: 100%;
    height: 100%;
    min-height: 0;
    overflow-y: auto;
    background: var(--color-background);
  }
  
  .dashboard-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: var(--space-normal);
    background: var(--color-surface);
    border-bottom: 1px solid var(--color-border);
  
    .header-left {
      display: flex;
      align-items: center;
      gap: var(--space-small);
    }
  
    .page-title {
      margin: 0;
      font-size: var(--font-size-large);
      font-weight: var(--font-weight-bold);
    }
  }
  
  .filters-section {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-normal);
    padding: var(--space-normal);
    background: var(--color-surface);
    border-bottom: 1px solid var(--color-border);
  
    .filter-group {
      display: flex;
      flex-direction: column;
      gap: var(--space-micro);
      min-width: 180px;
  
      label {
        font-size: var(--font-size-small);
        font-weight: var(--font-weight-medium);
        color: var(--s-600);
      }
  
      select, input {
        padding: var(--space-small);
        border: 1px solid var(--color-border);
        border-radius: var(--border-radius-normal);
        font-size: var(--font-size-small);
        background: var(--color-surface);
  
        &:focus {
          border-color: var(--w-500);
          outline: none;
        }
      }
    }
  
    .date-range {
      display: flex;
      flex-direction: row;
      gap: var(--space-small);
  
      .date-input {
        display: flex;
        flex-direction: column;
        gap: var(--space-micro);
      }
    }
  }
  
  .loading-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 400px;
    gap: var(--space-normal);
    color: var(--s-500);
  }
  
  .dashboard-content {
    padding: var(--space-normal);
  }
  
  .metrics-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: var(--space-normal);
    margin-bottom: var(--space-large);
  }
  
  .metric-card {
    display: flex;
    align-items: center;
    gap: var(--space-normal);
    padding: var(--space-normal);
    background: var(--color-surface);
    border-radius: var(--border-radius-large);
    box-shadow: var(--shadow-small);
    transition: transform 0.2s ease, box-shadow 0.2s ease;
  
    &:hover {
      transform: translateY(-2px);
      box-shadow: var(--shadow-medium);
    }
  
    .metric-icon {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 48px;
      height: 48px;
      border-radius: var(--border-radius-normal);
      color: var(--white);

      &.tickets-icon { background: linear-gradient(135deg, #3b82f6, #1d4ed8); }
      &.open-icon { background: linear-gradient(135deg, #f59e0b, #d97706); }
      &.closed-icon { background: linear-gradient(135deg, #10b981, #059669); }
      &.sales-icon { background: linear-gradient(135deg, #8b5cf6, #7c3aed); }
      &.critical-icon { background: linear-gradient(135deg, #7f1d1d, #dc2626); }
      &.high-icon { background: linear-gradient(135deg, #ef4444, #dc2626); }
      &.avg-icon { background: linear-gradient(135deg, #6366f1, #4f46e5); }
    }
  
    .metric-info {
      display: flex;
      flex-direction: column;
  
      .metric-value {
        font-size: var(--font-size-large);
        font-weight: var(--font-weight-bold);
        color: var(--s-900);
  
        &.sales-value {
          color: var(--g-500);
        }
      }
  
      .metric-label {
        font-size: var(--font-size-small);
        color: var(--s-500);
      }
    }
  }
  
  .charts-section {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: var(--space-normal);
    align-items: start;

    @media (max-width: 1024px) {
      grid-template-columns: 1fr;
    }
  }
  
  .chart-card {
    background: var(--color-surface);
    border-radius: var(--border-radius-large);
    box-shadow: var(--shadow-small);
    padding: var(--space-normal);
  
    &.small-chart {
      max-height: 350px;
    }
  
    .chart-title {
      margin: 0 0 var(--space-normal) 0;
      font-size: var(--font-size-medium);
      font-weight: var(--font-weight-bold);
      color: var(--s-800);
    }
  
    .chart-container {
      height: 250px;
      position: relative;
      overflow: hidden;
      border-radius: var(--border-radius-normal);
    }
  
    .no-data {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 100%;
      color: var(--s-400);
      font-style: italic;
    }
  }
  
  .priority-list {
    display: flex;
    flex-direction: column;
    gap: var(--space-small);
  
    .priority-item {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: var(--space-small);
      background: var(--s-25);
      border-radius: var(--border-radius-normal);
  
      .priority-badge {
        padding: var(--space-micro) var(--space-small);
        border-radius: var(--border-radius-small);
        font-size: var(--font-size-mini);
        font-weight: var(--font-weight-medium);
        color: var(--white);

        &.critical { background: #7f1d1d; }
        &.high { background: #ef4444; }
        &.medium { background: #f59e0b; }
        &.low { background: #10b981; }
      }
  
      .priority-count {
        font-weight: var(--font-weight-bold);
        color: var(--s-700);
      }
    }
  }
  
  .agents-list {
    display: flex;
    flex-direction: column;
    gap: var(--space-small);
    max-height: 250px;
    overflow-y: auto;
  
    .agent-item {
      display: flex;
      align-items: center;
      gap: var(--space-small);
      padding: var(--space-small);
      background: var(--s-25);
      border-radius: var(--border-radius-normal);
  
      .agent-rank {
        font-weight: var(--font-weight-bold);
        color: var(--w-500);
        min-width: 24px;
      }
  
      .agent-name {
        flex: 1;
        font-weight: var(--font-weight-medium);
        color: var(--s-800);
      }
  
      .agent-tickets {
        font-size: var(--font-size-small);
        color: var(--s-500);
      }
    }
  }
  </style>
  